import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  Row,
  Col,
  Alert,
} from 'antd';

import Loader from '../../commons/components/Loader';
import config from '../../config';
import { AuthenticationContext } from '../../authentications/contexts/AuthenticationContext';
import useDetail from '../../api/hooks/useDetail';

const EditView = (props) => {
  const {
    title,
    serverPath,
    component,
    FormComponent,
  } = props;

  const [serverErrors, setServerErrors] = React.useState(null);

  const { currentUser } = React.useContext(AuthenticationContext);

  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const fullPath = React.useMemo(() => `v2/component_types/${serverPath}`, [serverPath]);

  const formObject = useForm();

  const {
    reset,
  } = formObject;

  const {
    load,
    loading,
    data,
    error,
  } = useDetail(fullPath, id);

  React.useEffect(() => {
    load();
  }, []);

  React.useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data]);

  const handleSubmit = async (newData) => {
    setServerErrors(null);
    try {
      await axios.put(`${config.baseApiUrl}${fullPath}/${id}`, {
        [component]: newData,
      }, {
        headers: {
          Authorization: `Bearer ${currentUser.accessToken}`,
        },
      });
      navigate(-1);
    }
    catch (e) {
      if (e.isAxiosError && e.response.status === 409) {
        setServerErrors(e.response.data.errors);
      }
      else {
        setServerErrors([t('commons.genericError')]);
      }
    }
  };

  const renderLoading = () => (
    <Row style={{ height: '100%' }} justify="center" align="middle">
      <Col>
        <Loader />
      </Col>
    </Row>
  );

  const renderError = () => (
    <Row style={{ height: '100%' }} justify="center" align="middle">
      <Col>
        <Alert message={JSON.stringify(error)} type="error" showIcon />
      </Col>
    </Row>
  );

  if (loading) {
    return renderLoading();
  }

  if (error) {
    return renderError();
  }

  return (
    <>
      <h1>{title}</h1>
      <FormComponent
        onSubmit={handleSubmit}
        serverErrors={serverErrors}
        formObject={formObject}
        buttonText={t('commons.actions.edit')}
      />
    </>
  );
};

export default EditView;

const propTypes = {
  serverPath: PropTypes.string.isRequired,
  component: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  FormComponent: PropTypes.any.isRequired,
};

EditView.propTypes = propTypes;
